import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, GripVertical, Loader2, UploadCloud, X } from "lucide-react";
import { api, adminApi } from "@/lib/sleek";

const STYLES = ["Traditional", "Transitional", "Modern"];

export default function ShowroomManager({ token }) {
  const c = adminApi(token);
  const [photos, setPhotos] = useState(null);
  const [order, setOrder] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [uploadStyle, setUploadStyle] = useState("Transitional");
  const [dragIdx, setDragIdx] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/showroom-photos");
    setPhotos(data);
    setOrder(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(files.length);
    for (const f of files) {
      const fd = new FormData();
      fd.append("files", f);
      fd.append("style", uploadStyle);
      try {
        await c.post("/admin/showroom-photos", fd);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
      setUploading((n) => n - 1);
    }
    toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added to the showroom.`);
    load();
  };

  const removePhoto = async (p) => {
    if (!window.confirm("Remove this photo from the Showroom page? The file stays on disk so it can be restored later.")) return;
    await c.delete(`/admin/showroom-photos/${p.id}`);
    toast.success("Photo removed.");
    load();
  };

  const setStyle = async (p, style) => {
    const { data } = await c.patch(`/admin/showroom-photos/${p.id}`, { style });
    setOrder((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    setPhotos((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    toast.success(`Tagged as ${style}.`);
  };

  const saveOrder = async (next) => {
    setOrder(next);
    try {
      await c.put("/admin/showroom-photos/reorder", { ids: next.map((p) => p.id) });
      toast.success("Order saved — live on the Showroom page (refresh it if already open).");
    } catch {
      toast.error("Could not save order — try again.");
      load();
    }
  };

  const movePhoto = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[idx], next[j]] = [next[j], next[idx]];
    saveOrder(next);
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDropTarget(null); return; }
    const next = [...order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(null);
    setDropTarget(null);
    saveOrder(next);
  };

  return (
    <div className="mt-6 bg-white border border-zinc-200" data-testid="showroom-manager">
      <div className="flex items-center justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Showroom Manager</div>
          <div className="text-sm text-zinc-600 mt-1">
            {photos ? `${photos.length} photos` : "Loading…"} · numbered <span className="font-medium text-zinc-900">left to right, row by row</span> — same as the Showroom page · drag or use arrows · saves automatically
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={uploadStyle}
            onChange={(e) => setUploadStyle(e.target.value)}
            className="border border-zinc-300 text-xs px-2 py-2.5 bg-white"
            title="Style tag applied to new uploads"
            data-testid="sm-upload-style"
          >
            {STYLES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button onClick={() => fileRef.current?.click()} disabled={uploading > 0}
            className="flex items-center gap-2 bg-zinc-950 text-white text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-zinc-800 transition disabled:opacity-50" data-testid="sm-upload-btn">
            {uploading > 0 ? <><Loader2 size={14} className="animate-spin" /> Uploading ({uploading} left)</> : <><UploadCloud size={14} /> Upload photos</>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="sm-file-input"
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      <div
        className="p-6"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); }}
      >
        {photos && photos.length === 0 && (
          <button onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-zinc-300 py-16 text-zinc-500 hover:border-zinc-950 hover:text-zinc-950 transition flex flex-col items-center gap-3" data-testid="sm-empty-dropzone">
            <UploadCloud size={28} />
            <div className="text-sm">Drop showroom photos here or click to upload.</div>
          </button>
        )}

        <div className="grid grid-cols-3 gap-4 items-start">
          {[0, 1, 2].map((c) => (
            <div key={c} className="flex flex-col gap-4">
              {order
                .map((p, idx) => ({ p, idx }))
                .filter(({ idx }) => idx % 3 === c)
                .map(({ p, idx }) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(idx)); e.dataTransfer.effectAllowed = "move"; setDragIdx(idx); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropTarget(idx); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(idx); }}
                    onDragEnd={() => { setDragIdx(null); setDropTarget(null); }}
                    className={`relative group cursor-grab active:cursor-grabbing border ${dropTarget === idx && dragIdx !== null && dragIdx !== idx ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200"} ${dragIdx === idx ? "opacity-40" : ""}`}
                    data-testid={`sm-photo-tile-${idx}`}
                  >
                    <img src={p.src} alt="" className="w-full h-auto object-cover bg-zinc-100 pointer-events-none" />
                    <span className="absolute top-1.5 left-1.5 bg-zinc-950/80 text-white text-[10px] font-mono px-1.5 py-0.5">{idx + 1}</span>
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                      <button onClick={() => movePhoto(idx, -1)} disabled={idx === 0} title="Move one spot earlier"
                        className="bg-zinc-950/70 text-white hover:bg-zinc-950 disabled:opacity-30 p-1" data-testid={`sm-move-left-${idx}`}>
                        <ArrowLeft size={13} />
                      </button>
                      <button onClick={() => movePhoto(idx, 1)} disabled={idx === order.length - 1} title="Move one spot later"
                        className="bg-zinc-950/70 text-white hover:bg-zinc-950 disabled:opacity-30 p-1" data-testid={`sm-move-right-${idx}`}>
                        <ArrowRight size={13} />
                      </button>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/80 to-transparent p-2 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical size={14} className="text-white/70 shrink-0" />
                      <select
                        value={p.style}
                        onChange={(e) => setStyle(p, e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="bg-white/90 text-zinc-950 text-[10px] px-1 py-0.5 border-0 min-w-0"
                        data-testid={`sm-style-select-${idx}`}
                      >
                        {STYLES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      <button onClick={() => removePhoto(p)} title="Remove from showroom" className="text-white/80 hover:text-red-400 p-1 shrink-0" data-testid={`sm-delete-photo-${idx}`}>
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
